import { api } from "./axios";
import { runSharedApiRequest } from "./apiRateLimit";
import type { Project } from "@/types/project";
import type { Document } from "@/types/document";
import type { Contact } from "@/types/contact";
import type {
  DataResearchProvider,
  DataResearchRequest,
  DataResearchResponse,
  DataResearchGeojsonRequest,
  DataResearchGeojsonResponse,
} from "@/types/data-research/provider";

export type ProjectData = {
  name: string;
  description?: string;
};

export const getProjects = async (): Promise<Project[]> => {
  return runSharedApiRequest<Project[]>(
    "projects:list",
    async () => {
      const response = await api.get("/project/projects/");
      return response.data;
    },
    { rateLimitCooldownMs: 60_000 }
  );
};

export const getProject = async (id: string): Promise<Project> => {
  return runSharedApiRequest<Project>(
    `project:${id}`,
    async () => {
      const response = await api.get(`/project/projects/${id}/`);
      return response.data;
    },
    { rateLimitCooldownMs: 60_000 }
  );
};

export const getDocuments = async (projectId: string): Promise<Document[]> => {
  const response = await api.get(`/project/projects/${projectId}/documents/`);
  return response.data;
};

export const getContacts = async (projectId: string): Promise<Contact[]> => {
  const response = await api.get(`/project/projects/${projectId}/contacts/`);
  return response.data;
};

export type ContactPayload = {
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  notes?: string;
};

export const createContact = async (
  projectId: string,
  data: ContactPayload
): Promise<Contact> => {
  const response = await api.post(`/project/projects/${projectId}/contacts/`, data);
  return response.data;
};

export const updateContact = async (
  projectId: string,
  contactId: number,
  data: Partial<ContactPayload>
): Promise<Contact> => {
  const response = await api.patch(`/project/projects/${projectId}/contacts/${contactId}/`, data);
  return response.data;
};

export const deleteContact = async (
  projectId: string,
  contactId: number
): Promise<void> => {
  await api.delete(`/project/projects/${projectId}/contacts/${contactId}/`);
};

export type DocumentCreatePayload = {
  name: string;
  description?: string;
  file: File;
  initialNotes?: string;
};

export const createDocument = async (
  projectId: string,
  data: DocumentCreatePayload
): Promise<Document> => {
  const formData = new FormData();
  formData.append("name", data.name);
  if (data.description) {
    formData.append("description", data.description);
  }
  if (data.initialNotes) {
    formData.append("initial_notes", data.initialNotes);
  }
  formData.append("file", data.file);

  const response = await api.post(`/project/projects/${projectId}/documents/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export type DocumentUpdatePayload = {
  name?: string;
  description?: string;
};

export const updateDocument = async (
  projectId: string,
  documentId: number,
  data: DocumentUpdatePayload
): Promise<Document> => {
  const formData = new FormData();
  if (typeof data.name === "string") {
    formData.append("name", data.name);
  }
  if (typeof data.description === "string") {
    formData.append("description", data.description);
  }

  const response = await api.patch(
    `/project/projects/${projectId}/documents/${documentId}/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

export const deleteDocument = async (
  projectId: string,
  documentId: number
): Promise<void> => {
  await api.delete(`/project/projects/${projectId}/documents/${documentId}/`);
};

export const uploadDocumentVersion = async (
  projectId: string,
  documentId: number,
  file: File,
  changeNotes?: string
): Promise<{
  id: number;
  version_number: number;
  file?: string;
  file_size?: number;
  file_size_display?: string;
  uploaded_at?: string;
  change_notes?: string;
  download_url?: string;
}> => {
  const formData = new FormData();
  formData.append("file", file);
  if (changeNotes) {
    formData.append("change_notes", changeNotes);
  }

  const response = await api.post(
    `/project/projects/${projectId}/documents/${documentId}/upload_version/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

export const archiveDocument = async (
  projectId: string,
  documentId: number
): Promise<{ message: string }> => {
  const response = await api.post(
    `/project/projects/${projectId}/documents/${documentId}/archive/`
  );
  return response.data;
};

export const restoreDocument = async (
  projectId: string,
  documentId: number
): Promise<{ message: string }> => {
  const response = await api.post(
    `/project/projects/${projectId}/documents/${documentId}/restore/`
  );
  return response.data;
};

export const deleteProject = async (id: string): Promise<void> => {
  await api.delete(`/project/projects/${id}/`);
};

export const createProject = async (data: ProjectData): Promise<Project> => {
  const response = await api.post("/project/projects/", data);
  return response.data;
};

export const updateProject = async (
  id: string,
  data: Partial<ProjectData>
): Promise<Project> => {
  const response = await api.patch(`/project/projects/${id}/`, data);
  return response.data;
};

export interface ProjectPreviewImageResponse {
  success: boolean;
  preview_image: string | null;
  preview_image_manual: boolean;
}

export const uploadProjectPreviewImage = async (
  projectId: string | number,
  file: File
): Promise<ProjectPreviewImageResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(
    `/project/projects/${projectId}/preview-image/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

export const clearProjectPreviewImage = async (
  projectId: string | number
): Promise<ProjectPreviewImageResponse> => {
  const response = await api.delete(`/project/projects/${projectId}/preview-image/`);
  return response.data;
};

export interface PasswordChangeData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface PasswordChangeResponse {
  message: string;
  requires_relogin: boolean;
}

export const changePassword = async (data: PasswordChangeData): Promise<PasswordChangeResponse> => {
  const response = await api.post("/users/users/change_password/", data);
  return response.data;
};

export interface AdminPasswordChangeData {
  new_password: string;
  confirm_password: string;
}

export interface AdminPasswordChangeResponse {
  message: string;
  user_id: number;
  user_email: string;
}

export const adminChangePassword = async (userId: string, data: AdminPasswordChangeData): Promise<AdminPasswordChangeResponse> => {
  const response = await api.post(`/users/users/${userId}/admin_change_password/`, data);
  return response.data;
};

// Profile API functions
export interface UserProfile {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  bio?: string;
  phone_number?: string;
  organization?: string;
  job_title?: string;
  expertise?: string;
  website?: string;
  github_username?: string;
  profile_picture?: string;
  default_zoom_level?: number;
  preferred_map_center?: {
    type: "Point";
    coordinates: [number, number];
  } | null;
  date_joined: string;
  last_login: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  is_active?: boolean;
  organization_memberships?: Array<{
    organization?: { id?: string } | string;
    organization_id?: string;
    role?: string;
    is_active?: boolean;
    web_access?: boolean;
  }>;
  /** Backend ENFORCE_WEB_ACCESS switch. Only `true` may log mobile-only users out. */
  web_access_enforced?: boolean;
  preferred_units: "metric" | "imperial";
  receive_notifications: boolean;
  two_factor_enabled?: boolean;
}

export const getCurrentUserProfile = async (): Promise<UserProfile> => {
  return runSharedApiRequest<UserProfile>(
    "users:me",
    async () => {
      const response = await api.get("/users/users/me/");
      return response.data;
    },
    { rateLimitCooldownMs: 60_000 }
  );
};

export const updateUserProfile = async (data: Partial<UserProfile>): Promise<UserProfile> => {
  const response = await api.patch("/users/users/update_profile/", data);
  return response.data;
};

export const uploadProfilePicture = async (file: File): Promise<UserProfile> => {
  const formData = new FormData();
  formData.append("profile_picture", file);

  const response = await api.post("/users/users/profile_picture/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

// Stats API functions
export interface UserStats {
  projects_count: number;
  layers_count: number;
  collaborators_count: number;
  analyses_count: number;
  projects_this_month: number;
  analyses_this_month: number;
}

export const getUserStats = async (): Promise<UserStats> => {
  const response = await api.get("/users/users/my_stats/");
  return response.data;
};

export const getDataResearchLayers = async (
  provider: DataResearchProvider
): Promise<{
  layers: { key: string; filename: string; exists: boolean }[];
  versions?: {
    mgi?: { default?: string; selected?: string; available?: string[] };
    demographics?: { default?: string; selected?: string; available?: string[] };
  };
}> => {
  const response = await api.get(`/data-research/${provider}/layers/`);
  return response.data;
};

export const runDataResearch = async (
  provider: DataResearchProvider,
  data: DataResearchRequest
): Promise<DataResearchResponse> => {
  const response = await api.post(`/data-research/${provider}/research/`, data);
  return response.data;
};

export const getDataResearchGeojson = async (
  provider: DataResearchProvider,
  data: DataResearchGeojsonRequest
): Promise<DataResearchGeojsonResponse> => {
  const response = await api.post(`/data-research/${provider}/geojson/`, data);
  return response.data;
};

export type CreateProjectLayerRequest = {
  name: string;
  description?: string;
  visible?: boolean;
  data: GeoJSON.FeatureCollection;
  style?: Record<string, any>;
};

export const createProjectLayer = async (
  projectId: string,
  data: CreateProjectLayerRequest
): Promise<unknown> => {
  const response = await api.post(`/project/projects/${projectId}/upload-layer/`, data);
  return response.data;
};

export const updateProjectLayerStyle = async (
  projectId: string,
  layerId: string,
  style: Record<string, any>,
  layerName?: string
): Promise<unknown> => {
  const response = await api.patch(`/project/projects/${projectId}/update_layer_style/`, {
    layer_id: layerId,
    style,
    ...(layerName ? { layer_name: layerName } : {}),
  });
  return response.data;
};

export type ChatCompletionRole = "system" | "user" | "assistant" | "tool";

export type ChatCompletionMessage = {
  role: ChatCompletionRole;
  content?: string | null;
  name?: string;
  tool_call_id?: string;
};

export type AnalyticsAgentContext = {
  geometry?: GeoJSON.Geometry | null;
  srid?: number;
  layers?: string[];
  relation?: "intersects" | "within" | "contains";
  buffer_m?: number;
  limit?: number;
  include_demographics?: boolean;
  mgi_version?: string;
  demographics_year?: string;
  project_id?: string;
  locale?: string;
};

export type AnalyticsAgentRequest = {
  model?: string;
  provider?: Record<string, unknown>;
  messages: ChatCompletionMessage[];
  context?: AnalyticsAgentContext;
};

export type AIConversation = {
  id: string;
  project_id: string | number;
  title: string;
  created_at: string;
  updated_at: string;
  last_message_at?: string | null;
  message_count?: number;
  last_message?: {
    role?: string;
    content?: string;
    created_at?: string;
  } | null;
};

export type AIMessage = {
  id: string;
  role: ChatCompletionRole;
  content: string;
  actions?: Array<Record<string, unknown>>;
  meta?: Record<string, unknown>;
  created_at: string;
};

export type ChatCompletionResponse = {
  id?: string;
  object?: string;
  created?: number;
  model?: string;
  choices?: Array<{
    index?: number;
    message?: {
      role?: string;
      content?: string | null;
      tool_calls?: Array<Record<string, unknown>>;
    };
    finish_reason?: string | null;
  }>;
};

export const runAnalyticsAgent = async (
  data: AnalyticsAgentRequest
): Promise<ChatCompletionResponse> => {
  const response = await api.post("/ai/chat/completions/", data);
  return response.data;
};

export const listAIConversations = async (
  projectId: string
): Promise<AIConversation[]> => {
  const query = encodeURIComponent(projectId);
  try {
    const response = await api.get(`/ai/conversations/?project_id=${query}`);
    return response.data;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 403 || status === 404) return [];
    throw error;
  }
};

export const createAIConversation = async (
  projectId: string,
  title?: string
): Promise<AIConversation> => {
  const response = await api.post("/ai/conversations/", {
    project_id: projectId,
    title,
  });
  return response.data;
};

export const listAIConversationMessages = async (
  conversationId: string
): Promise<AIMessage[]> => {
  const response = await api.get(`/ai/conversations/${conversationId}/messages/`);
  return response.data;
};

export const createAIConversationMessage = async (
  conversationId: string,
  data: Pick<AIMessage, "role" | "content"> & {
    actions?: Array<Record<string, unknown>>;
    meta?: Record<string, unknown>;
  }
): Promise<AIMessage> => {
  const response = await api.post(`/ai/conversations/${conversationId}/messages/`, data);
  return response.data;
};

export type ApprovalExecuteRequest = {
  approval_id: string;
  tool_name: string;
  tool_args: Record<string, unknown>;
  project_id: string;
};

export type ApprovalExecuteResponse = {
  approval_id: string;
  tool_name: string;
  status: "success" | "error";
  result?: Record<string, unknown>;
  detail?: string;
  code?: string;  // e.g., "TOKEN_INVALID" for expired/invalid tokens
};

export const executeApprovedTool = async (
  data: ApprovalExecuteRequest
): Promise<ApprovalExecuteResponse> => {
  const response = await api.post("/ai/tools/execute-approved/", data);
  return response.data;
};
